import { ISnackbarInput } from "../Components/CustomUIElements/StyledSnackbar";
import { GenericTableState } from "../Components/GenericTable/genericTableReducer";
import { TableActionDrawerAction, TableActionItemAction, TableColumnAction, TableDensity, TablePaginationAction, TableRowRemoveSelectedAction, TableRowSelectedAction, TableScrollAction, TableSelectionAction, TPaginationState, TTableState } from "../Components/GenericTable/types";
import type { ICandidateGroup } from "../CandidateGroup/types";
export type { ICandidateGroup } from "../CandidateGroup/types";
import { candidateGroupTableColumn } from "./CandidateGroupTableRows";

export interface YearsOfExperienceRange {
  min?: number | null;
  max?: number | null;
}

export interface ICandidateSourcingFilters {
  job_titles?: string[];
  skills?: string[][];
  industries?: string[];
  years_of_experience?: YearsOfExperienceRange;
  locations?: string[];
}

export interface ICandidateSourcingSearch {
  id: number;
  query: string;
  name: string;
  filters: ICandidateSourcingFilters;
  executed_at: string;
  filter_options?: {
    job_titles?: string[];
    skills?: string[];
    industries?: string[];
    location?: string[];
  };
  job?: {
    id: number;
    reference: string;
    title: string;
  };
  screened_candidates_count?: number;
}

export interface IApiResponse {
  res: {
    search?: ICandidateSourcingSearch;
    jobs?: any[];
    candidates?: any[];
  };
  resHead?: {
    [key: string]: string;
  };
}

export interface ICandidateGroupsApiResponse {
  res: {
    candidate_groups: ICandidateGroup[];
  };
  resHead?: {
    [key: string]: string;
  };
}

export interface IJob {
  id: number;
  reference: string;
  title: string;
}
export interface ISearchFilter {
  job_title: string[];
  skills: string[];
  industries: string[];
  years_of_experience: YearsOfExperienceRange;
  location: string;
}

export const filterToLabel = {
  job_titles: 'Job Titles',
  skills: 'Skills',
  industries: 'Industries',
  years_of_experience: 'Years of Experience',
  locations: 'Locations'
} as const;

export type FilterKey = keyof typeof filterToLabel;

export interface CandidatesState {
  snackbar: {
    message: string;
    state: 'success' | 'warning' | 'error';
  };
  candidatesTableState: TTableState<ICandidateGroup, typeof candidateGroupTableColumn>;
  candidatesTableSettings: TPaginationState<(typeof candidateGroupTableColumn)[number]['id']>;
  searchQuery: string;
  totalPages: number;
  candidatesCount: number;
  optionsCleared: boolean;
  isJobDialogOpen: boolean;
  typesToExpand: string[];
  progress: number;
  currentStep: string;
  totalSteps: number;
  transitioningStep: boolean;
  progressVisible: boolean;
  fetchingAssessedPercentage: boolean;
  isAddToCandidatePoolOpen: boolean;
  showResults: boolean;
}

export type CandidatesAction =
  | { type: 'INCREMENT_PROGRESS'; payload: { targetProgress: number } }
  | { type: 'SET_OPTIONS_CLEARED'; payload: boolean }
  | { type: 'SET_JOB_DIALOG_OPEN'; payload: boolean }
  | { type: 'SET_TYPES_TO_EXPAND'; payload: string[] }
  | { type: 'SET_SNACKBAR'; payload: ISnackbarInput }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_DENSITY'; payload: TableDensity }
  | { type: 'SET_DEFAULT_DATA'; payload: GenericTableState }
  | { type: 'SET_TOTAL_PAGES'; payload: number }
  | { type: 'SET_CANDIDATES_COUNT'; payload: number }
  | { type: 'SET_PROGRESS'; payload: number }
  | { type: 'SET_CURRENT_STEP'; payload: string }
  | { type: 'SET_TRANSITIONING_STEP'; payload: boolean }
  | { type: 'SET_PROGRESS_VISIBLE'; payload: boolean }
  | { type: 'START_SEARCH'; payload: { typesToExpand: string[] } }
  | { type: 'SET_SELECTED_ITEM_IDS'; payload: number[] }
  | { type: 'COMPLETE_EXECUTING_SEARCH' }
  | { type: 'COMPLETE_PROGRESS' }
  | { type: 'OPEN_ADD_TO_POOL_DIALOG' }
  | { type: 'CLOSE_ADD_TO_POOL_DIALOG' }
  | TableScrollAction
  | TableSelectionAction<ICandidateGroup>
  | TableColumnAction<typeof candidateGroupTableColumn>
  | TablePaginationAction<(typeof candidateGroupTableColumn)[number]['id']>
  | TableActionItemAction<ICandidateGroup>
  | TableActionDrawerAction
  | TableRowSelectedAction
  | TableRowRemoveSelectedAction;
